import React, { createContext, useContext, useState, useEffect } from 'react';

// Get authentication storage keys from environment variables
const AUTH_TOKEN_KEY = process.env.REACT_APP_AUTH_STORAGE_KEY;
const AUTH_USER_KEY = process.env.REACT_APP_AUTH_USER_KEY || `${AUTH_TOKEN_KEY}_user`;

// Verify required environment variables are set
if (!AUTH_TOKEN_KEY) {
  throw new Error('REACT_APP_AUTH_STORAGE_KEY environment variable is not set');
}

interface User {
  id: string;
  username: string;
  email: string;
  fullName?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  token: null,
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem(AUTH_TOKEN_KEY));
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem(AUTH_USER_KEY);
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);

  // Effect to update authentication state if token changes
  useEffect(() => {
    setIsAuthenticated(!!token);
  }, [token]);

  const login = (newToken: string, userData: User) => {
    localStorage.setItem(AUTH_TOKEN_KEY, newToken);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
